import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  RefreshCw, CheckCircle, XCircle, Package, Users, FileText, 
  Building2, ArrowUpDown, Search, AlertTriangle, Cloud, CloudOff,
  Download, Upload, ExternalLink
} from "lucide-react";
import axios from "axios";

export default function ZohoIntegrationPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockCheck, setStockCheck] = useState({ name: "", quantity: 1, result: null });

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/zoho/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch Zoho dashboard:", error);
      toast.error("Failed to load Zoho dashboard");
    }
  }, [token]);

  const fetchInventory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/zoho/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data.items || []);
    } catch (error) {
      console.error("Failed to fetch inventory:", error);
    }
  }, [token]);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/zoho/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
    }
  }, [token]);

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/zoho/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchDashboard();
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard]);

  const handleSyncCustomers = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/zoho/sync-all-customers`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Synced ${response.data.synced} customers to Zoho`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} customers failed to sync`);
      }
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to sync customers");
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncInventory = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/zoho/sync-inventory`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Synced ${response.data.synced} inventory items`);
      fetchInventory();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to sync inventory");
    } finally {
      setSyncing(false);
    }
  };

  const handleCheckStock = async () => {
    if (!stockCheck.name) {
      toast.error("Enter item name to check");
      return;
    }
    try {
      const response = await axios.post(`${API}/zoho/check-stock`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { item_name: stockCheck.name, quantity: stockCheck.quantity }
      });
      setStockCheck(prev => ({ ...prev, result: response.data }));
    } catch (error) {
      toast.error("Failed to check stock");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  const isConnected = dashboard?.status === "connected";

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="zoho-integration-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <img 
                src="https://www.zoho.com/sites/zweb/images/zoho_general_pages/zoho-logo.svg" 
                alt="Zoho" 
                className="h-8"
              />
              Zoho Books Integration
            </h1>
            <p className="text-slate-500 mt-1">Manage inventory, customers, and invoices</p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchDashboard}
            data-testid="refresh-zoho-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Connection Status Card */}
        <Card className={isConnected ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/50"}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isConnected ? (
                  <Cloud className="h-8 w-8 text-green-600" />
                ) : (
                  <CloudOff className="h-8 w-8 text-red-600" />
                )}
                <div>
                  <CardTitle className="text-lg">
                    {isConnected ? "Connected to Zoho Books" : "Not Connected"}
                  </CardTitle>
                  <CardDescription>
                    {dashboard?.organization?.name || "Connect to sync data"}
                  </CardDescription>
                </div>
              </div>
              <Badge variant={isConnected ? "default" : "destructive"} className="text-sm px-3 py-1">
                {isConnected ? (
                  <><CheckCircle className="h-4 w-4 mr-1" /> Active</>
                ) : (
                  <><XCircle className="h-4 w-4 mr-1" /> Disconnected</>
                )}
              </Badge>
            </div>
          </CardHeader>
          {isConnected && dashboard?.organization && (
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Organization</span>
                  <p className="font-medium">{dashboard.organization.name}</p>
                </div>
                <div>
                  <span className="text-slate-500">Plan</span>
                  <p className="font-medium">{dashboard.organization.plan}</p>
                </div>
                <div>
                  <span className="text-slate-500">Currency</span>
                  <p className="font-medium">{dashboard.organization.currency}</p>
                </div>
                <div>
                  <span className="text-slate-500">Email</span>
                  <p className="font-medium">{dashboard.organization.email}</p>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Stats Grid */}
        {isConnected && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Zoho Inventory</p>
                    <p className="text-2xl font-bold">{dashboard?.zoho_counts?.inventory_items || 0}</p>
                  </div>
                  <Package className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Zoho Contacts</p>
                    <p className="text-2xl font-bold">{dashboard?.zoho_counts?.contacts || 0}</p>
                  </div>
                  <Users className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Zoho Invoices</p>
                    <p className="text-2xl font-bold">{dashboard?.zoho_counts?.invoices || 0}</p>
                  </div>
                  <FileText className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Patients Synced</p>
                    <p className="text-2xl font-bold">
                      {dashboard?.local_sync?.patients_synced || 0}
                      <span className="text-sm text-slate-400 font-normal">
                        /{dashboard?.local_sync?.patients_total || 0}
                      </span>
                    </p>
                  </div>
                  <ArrowUpDown className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Sync Actions */}
        {isConnected && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sync Actions</CardTitle>
              <CardDescription>Sync data between HearClear and Zoho Books</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleSyncCustomers} 
                  disabled={syncing}
                  data-testid="sync-customers-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Sync Patients to Zoho
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleSyncInventory} 
                  disabled={syncing}
                  data-testid="sync-inventory-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Sync Inventory from Zoho
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stock Check */}
        {isConnected && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5" />
                Check Stock Availability
              </CardTitle>
              <CardDescription>Check Zoho inventory before closing a deal</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4">
                <Input
                  placeholder="Item name (e.g., Signia Pure)"
                  value={stockCheck.name}
                  onChange={(e) => setStockCheck(prev => ({ ...prev, name: e.target.value }))}
                  className="flex-1"
                  data-testid="stock-check-input"
                />
                <Input
                  type="number"
                  placeholder="Quantity"
                  value={stockCheck.quantity}
                  onChange={(e) => setStockCheck(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  className="w-24"
                  min={1}
                />
                <Button onClick={handleCheckStock} data-testid="check-stock-btn">
                  <Search className="h-4 w-4 mr-2" />
                  Check Stock
                </Button>
              </div>
              {stockCheck.result && (
                <div className={`mt-4 p-4 rounded-lg ${stockCheck.result.available ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {stockCheck.result.available ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    )}
                    <span className={`font-medium ${stockCheck.result.available ? 'text-green-700' : 'text-red-700'}`}>
                      {stockCheck.result.reason}
                    </span>
                  </div>
                  {stockCheck.result.item_name && (
                    <div className="mt-2 text-sm text-slate-600">
                      <p><strong>Item:</strong> {stockCheck.result.item_name}</p>
                      <p><strong>SKU:</strong> {stockCheck.result.sku || 'N/A'}</p>
                      <p><strong>Stock:</strong> {stockCheck.result.stock} units</p>
                      <p><strong>Requested:</strong> {stockCheck.result.requested} units</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Data Tabs */}
        {isConnected && (
          <Tabs defaultValue="inventory" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="inventory" onClick={fetchInventory}>
                <Package className="h-4 w-4 mr-2" />
                Inventory
              </TabsTrigger>
              <TabsTrigger value="contacts" onClick={fetchContacts}>
                <Users className="h-4 w-4 mr-2" />
                Contacts
              </TabsTrigger>
              <TabsTrigger value="invoices" onClick={fetchInvoices}>
                <FileText className="h-4 w-4 mr-2" />
                Invoices
              </TabsTrigger>
            </TabsList>

            <TabsContent value="inventory">
              <Card>
                <CardHeader>
                  <CardTitle>Zoho Inventory Items</CardTitle>
                  <CardDescription>{inventory.length} items loaded</CardDescription>
                </CardHeader>
                <CardContent>
                  {inventory.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">Click the tab to load inventory from Zoho</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>SKU</TableHead>
                            <TableHead className="text-right">Rate</TableHead>
                            <TableHead className="text-right">Stock</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {inventory.slice(0, 20).map((item) => (
                            <TableRow key={item.item_id}>
                              <TableCell className="font-medium">{item.name}</TableCell>
                              <TableCell className="text-slate-500">{item.sku || '-'}</TableCell>
                              <TableCell className="text-right">{formatCurrency(item.rate)}</TableCell>
                              <TableCell className="text-right">{item.stock_on_hand || 0}</TableCell>
                              <TableCell>
                                <Badge variant={item.status === 'active' ? 'default' : 'secondary'}>
                                  {item.status || 'active'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {inventory.length > 20 && (
                        <p className="text-center text-slate-500 py-4 text-sm">
                          Showing 20 of {inventory.length} items
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="contacts">
              <Card>
                <CardHeader>
                  <CardTitle>Zoho Contacts</CardTitle>
                  <CardDescription>{contacts.length} contacts loaded</CardDescription>
                </CardHeader>
                <CardContent>
                  {contacts.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">Click the tab to load contacts from Zoho</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Phone</TableHead>
                            <TableHead className="text-right">Outstanding</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {contacts.slice(0, 20).map((contact) => (
                            <TableRow key={contact.contact_id}>
                              <TableCell className="font-medium">{contact.contact_name}</TableCell>
                              <TableCell className="text-slate-500">{contact.email || '-'}</TableCell>
                              <TableCell>{contact.phone || '-'}</TableCell>
                              <TableCell className="text-right">{formatCurrency(contact.outstanding_receivable_amount)}</TableCell>
                              <TableCell>
                                <Badge variant={contact.status === 'active' ? 'default' : 'secondary'}>
                                  {contact.status || 'active'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="invoices">
              <Card>
                <CardHeader>
                  <CardTitle>Zoho Invoices</CardTitle>
                  <CardDescription>{invoices.length} invoices loaded</CardDescription>
                </CardHeader>
                <CardContent>
                  {invoices.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">Click the tab to load invoices from Zoho</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice #</TableHead>
                            <TableHead>Customer</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead className="text-right">Balance</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoices.slice(0, 20).map((invoice) => (
                            <TableRow key={invoice.invoice_id}>
                              <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                              <TableCell className="text-slate-500">{invoice.customer_name}</TableCell>
                              <TableCell>{invoice.date}</TableCell>
                              <TableCell className="text-right">{formatCurrency(invoice.total)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(invoice.balance)}</TableCell>
                              <TableCell>
                                <Badge 
                                  variant={invoice.status === 'paid' ? 'default' : invoice.status === 'overdue' ? 'destructive' : 'secondary'}
                                >
                                  {invoice.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Not Connected State */}
        {!isConnected && (
          <Card className="text-center py-12">
            <CardContent>
              <CloudOff className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">Zoho Books Not Connected</h3>
              <p className="text-slate-500 mb-6">Connect to Zoho Books to sync inventory, customers, and invoices</p>
              <Button asChild>
                <a href="https://api-console.zoho.in" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Zoho API Console
                </a>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
